import { useMemo } from 'react';
import { CheckCircle2, Loader2, FolderOpen, User, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { TodoItemResponse, TodoSectionResponse } from '@/lib/api';

function formatCompletionDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const itemDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (itemDate.getTime() === today.getTime()) return 'Today';
  if (itemDate.getTime() === yesterday.getTime()) return 'Yesterday';
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: itemDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function groupByDate(items: TodoItemResponse[]): Map<string, TodoItemResponse[]> {
  const groups = new Map<string, TodoItemResponse[]>();
  for (const item of items) {
    const key = item.completedAt ? formatCompletionDate(item.completedAt) : 'Unknown';
    const existing = groups.get(key);
    if (existing) {
      existing.push(item);
    } else {
      groups.set(key, [item]);
    }
  }
  return groups;
}

interface CompletedTimelineProps {
  items: TodoItemResponse[];
  sections?: TodoSectionResponse[];
  /** Optional pagination — V-1 fetches a fixed limit and omits both. */
  hasMore?: boolean;
  onLoadMore?: () => void;
  loading: boolean;
  /** When true, renders "completed by <name>" using `resolveCompletedBy`
   * to map principalId → display name. Set by the caller depending on
   * whether the context has access to the resolver (private auth'd
   * view does; public view doesn't). */
  showCompletedBy?: boolean;
  /** principalId → display label. Caller supplies (see
   * usePrincipalDisplayNames). Returning '' means "don't render
   * attribution for this item". */
  resolveCompletedBy?: (principalId: string | null | undefined) => string;
  onUncomplete?: (item: TodoItemResponse) => void;
}

export function CompletedTimeline({
  items,
  sections = [],
  hasMore = false,
  onLoadMore,
  loading,
  showCompletedBy = false,
  resolveCompletedBy,
  onUncomplete,
}: CompletedTimelineProps) {
  const groups = groupByDate(items);

  // Build a lookup from sectionId to category name (only category sections, not timeframes)
  const categoryMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of sections) {
      if (s.sectionType === 'category') {
        map.set(s.sectionId, s.name);
      }
    }
    return map;
  }, [sections]);

  if (items.length === 0 && !loading) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">No completed items yet.</p>
    );
  }

  return (
    <div className="space-y-6">
      {Array.from(groups.entries()).map(([dateLabel, dateItems]) => (
        <div key={dateLabel}>
          <h4 className="text-sm font-medium text-muted-foreground mb-3">{dateLabel}</h4>
          <div className="divide-y divide-border">
            {dateItems.map((item) => {
              const categoryName = item.sectionId ? categoryMap.get(item.sectionId) : null;

              return (
                <div key={item.itemId} className="group/item flex items-center gap-3 px-3 py-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-foreground truncate">{item.title}</span>
                  {categoryName && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
                      <FolderOpen className="h-3 w-3" />
                      {categoryName}
                    </span>
                  )}
                  {showCompletedBy &&
                    item.completedByPrincipalId &&
                    resolveCompletedBy &&
                    (() => {
                      const label = resolveCompletedBy(item.completedByPrincipalId);
                      return label ? (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
                          <User className="h-3 w-3" />
                          {label}
                        </span>
                      ) : null;
                    })()}
                  {item.completedAt && (
                    <span className="text-xs text-muted-foreground flex-shrink-0 ml-auto">
                      {formatDateTime(item.completedAt)}
                    </span>
                  )}
                  {onUncomplete && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 flex-shrink-0 opacity-0 group-hover/item:opacity-100 transition-opacity"
                      onClick={() => onUncomplete(item)}
                      aria-label={`Move "${item.title}" back to todo`}
                      title="Move back to todo"
                    >
                      <Undo2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {hasMore && onLoadMore && (
        <div className="flex justify-center pt-2">
          <Button variant="outline" size="sm" onClick={onLoadMore} disabled={loading}>
            {loading && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
            Load more
          </Button>
        </div>
      )}
    </div>
  );
}
